// theme.js - Custom React Admin Theme
import { createTheme } from '@mui/material/styles';

// Your brand colors
const brandColors = {
  primaryDarkGreen: '#173131',
  accentRed: '#5F011E',
  goldAccent: '#A59063',
  sandPink: '#EDDDD3',
  paperWhite: '#FAFFF0',
};

// Light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColors.primaryDarkGreen,
      light: '#2a4040',
      dark: '#0d1a1a',
      contrastText: brandColors.paperWhite,
    },
    secondary: {
      main: brandColors.goldAccent,
      light: '#b8a376',
      dark: '#8a7851',
      contrastText: brandColors.paperWhite,
    },
    error: {
      main: brandColors.accentRed,
      light: '#8b1e3d',
      dark: '#3d0112',
      contrastText: brandColors.paperWhite,
    },
    background: {
      default: brandColors.paperWhite,
      paper: '#EFF4E5',
    },
    text: {
      primary: brandColors.primaryDarkGreen,
      secondary: brandColors.accentRed,
    },
    divider: brandColors.sandPink,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
    h1: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    h5: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h6: {
      color: brandColors.primaryDarkGreen,
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      lineHeight: 1.5,
    },
  },
  components: {
    // Global CSS override to ensure Inter is used everywhere
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': {
          fontFamily: 'Inter',
          src: 'url("/Inter-VariableFont_opsz,wght.ttf") format("truetype")',
          fontWeight: '100 900',
          fontStyle: 'normal',
          fontDisplay: 'swap',
        },
        '*': {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif !important',
        },
        body: {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
        },
      },
    },
    // Typography
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // App Bar (top navigation)
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primaryDarkGreen,
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: brandColors.sandPink,
          borderRight: `1px solid ${brandColors.goldAccent}`,
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Cards and Papers
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#EFF4E5',
          fontFamily: '"Inter", sans-serif',
          '&.MuiCard-root': {
            boxShadow: '0 2px 8px rgba(23, 49, 49, 0.08)',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
          },
        },
      },
    },
    // Box component styling for special classes
    MuiBox: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '&.protected-info': {
            backgroundColor: '#f8faf8 !important',
            border: '1px solid #d4e6d4 !important',
          },
        },
      },
    },
    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '6px',
        },
        containedPrimary: {
          backgroundColor: brandColors.primaryDarkGreen,
          color: brandColors.paperWhite,
          fontWeight: 500,
          '&:hover': {
            backgroundColor: brandColors.accentRed,
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(95, 1, 30, 0.3)',
          },
        },
        containedSecondary: {
          backgroundColor: brandColors.goldAccent,
          color: brandColors.paperWhite,
          fontWeight: 500,
          '&:hover': {
            backgroundColor: '#8a7851',
          },
        },
      },
    },
    // Tables
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.sandPink,
          fontFamily: '"Inter", sans-serif',
          '& .MuiTableCell-head': {
            color: brandColors.primaryDarkGreen,
            fontWeight: 600,
            fontFamily: '"Inter", sans-serif',
            borderBottom: `2px solid ${brandColors.goldAccent}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '&:nth-of-type(odd)': {
            backgroundColor: `${brandColors.sandPink}30`, // 30 = 30% opacity
          },
          '&:hover': {
            backgroundColor: `${brandColors.sandPink}50`, // 50% opacity
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Form inputs
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%', // Make all text fields full width
          fontFamily: '"Inter", sans-serif',
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#EFF4E5',
            fontFamily: '"Inter", sans-serif',
            '& fieldset': {
              borderColor: brandColors.sandPink,
            },
            '&:hover fieldset': {
              borderColor: brandColors.goldAccent,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.goldAccent,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
          },
          '& .MuiInputBase-input': {
            fontFamily: '"Inter", sans-serif',
          },
        },
      },
    },
    // Form Control (for dropdowns, etc.)
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%', // Make form controls full width
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Select fields
    MuiSelect: {
      styleOverrides: {
        root: {
          width: '100%',
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Input Labels
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
        },
      },
    },
    // List items (sidebar navigation)
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: brandColors.primaryDarkGreen,
          fontFamily: '"Inter", sans-serif',
          '&:hover': {
            backgroundColor: brandColors.goldAccent,
            color: brandColors.paperWhite,
          },
          '&.Mui-selected': {
            backgroundColor: brandColors.primaryDarkGreen,
            color: brandColors.paperWhite,
            '&:hover': {
              backgroundColor: brandColors.accentRed,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
        },
        primary: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
        },
        secondary: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Tab components
    MuiTab: {
      styleOverrides: {
        root: {
          color: brandColors.primaryDarkGreen,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          '&.Mui-selected': {
            color: brandColors.goldAccent,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brandColors.goldAccent,
        },
      },
    },
    // Chips
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
        },
        label: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // MenuItem
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: brandColors.accentRed,        // Red for primary actions/selections
      light: '#8b1e3d',
      dark: '#3d0112',
      contrastText: brandColors.paperWhite,
    },
    secondary: {
      main: brandColors.goldAccent,       // Gold for secondary elements
      light: '#b8a376',
      dark: '#8a7851',
      contrastText: brandColors.paperWhite,
    },
    error: {
      main: brandColors.accentRed,
      light: '#8b1e3d',
      dark: '#3d0112',
      contrastText: brandColors.paperWhite,
    },
    background: {
      default: '#0a1515',
      paper: '#1f3333',
    },
    text: {
      primary: brandColors.paperWhite,
      secondary: brandColors.sandPink,
    },
    divider: '#2a4040',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
    allVariants: {
      color: brandColors.paperWhite,
      fontFamily: '"Inter", sans-serif',
    },
    h1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 400,
      lineHeight: 1.5,
    },
  },
  components: {
    // Global CSS override to ensure Inter is used everywhere
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': {
          fontFamily: 'Inter',
          src: 'url("/Inter-VariableFont_opsz,wght.ttf") format("truetype")',
          fontWeight: '100 900',
          fontStyle: 'normal',
          fontDisplay: 'swap',
        },
        '*': {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif !important',
        },
        body: {
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
          backgroundColor: '#0a1515',
          color: brandColors.paperWhite,
        },
      },
    },
    // Typography
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          color: brandColors.paperWhite,
        },
      },
    },
    // App Bar
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.primaryDarkGreen,
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0d1a1a',
          borderRight: '1px solid #2a4040',
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Cards and Papers
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1f3333',
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
          '&.MuiCard-root': {
            backgroundColor: '#243838',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #2a4040',
            borderRadius: '8px',
          },
        },
      },
    },
    // Box component styling for special classes
    MuiBox: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '&.protected-info': {
            backgroundColor: '#0f1d1d !important',
            border: '1px solid #1a2a2a !important',
          },
        },
      },
    },
    // Icon buttons (like edit/upload)
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: brandColors.paperWhite + ' !important',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          fontFamily: '"Inter", sans-serif',
          '&:hover': {
            backgroundColor: brandColors.accentRed + ' !important',
            color: brandColors.paperWhite + ' !important',
          },
          // React Admin specific button classes
          '&.RaEditButton-root': {
            color: brandColors.paperWhite + ' !important',
          },
          '&.MuiButton-root': {
            color: brandColors.paperWhite + ' !important',
            minWidth: 'auto',
          },
        },
      },
    },
    // Regular buttons that might be used as icon buttons
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '6px',
          // For buttons without labels (icon only)
          '&.MuiButton-root': {
            minWidth: 'auto',
            color: brandColors.paperWhite,
          },
        },
        containedPrimary: {
          backgroundColor: brandColors.accentRed,
          color: brandColors.paperWhite,
          fontWeight: 500,
          '&:hover': {
            backgroundColor: '#7a0225',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(95, 1, 30, 0.3)',
          },
        },
        // Text buttons (like EditButton without labels)
        text: {
          color: brandColors.paperWhite + ' !important',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: brandColors.accentRed + ' !important',
            color: brandColors.paperWhite + ' !important',
          },
        },
      },
    },
    // Fab buttons (floating action buttons)
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.goldAccent,
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
          '&:hover': {
            backgroundColor: brandColors.accentRed,
          },
        },
      },
    },
    // Tables
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#0d1a1a',
          fontFamily: '"Inter", sans-serif',
          '& .MuiTableCell-head': {
            color: brandColors.paperWhite,
            fontWeight: 600,
            fontFamily: '"Inter", sans-serif',
            borderBottom: `2px solid ${brandColors.goldAccent}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '&:nth-of-type(odd)': {
            backgroundColor: '#0d1a1a',
          },
          '&:hover': {
            backgroundColor: '#2a4040',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          color: brandColors.paperWhite,
        },
      },
    },
    // Form inputs
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%', // Make all text fields full width
          fontFamily: '"Inter", sans-serif',
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#243838',
            color: brandColors.paperWhite,
            fontFamily: '"Inter", sans-serif',
            '& fieldset': {
              borderColor: '#2a4040',
            },
            '&:hover fieldset': {
              borderColor: brandColors.goldAccent,
            },
            '&.Mui-focused fieldset': {
              borderColor: brandColors.goldAccent,
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            color: brandColors.paperWhite,
          },
          '& .MuiInputBase-input': {
            fontFamily: '"Inter", sans-serif',
            color: brandColors.paperWhite,
          },
        },
      },
    },
    // Form Control (for dropdowns, etc.)
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%', // Make form controls full width
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    // Select fields
    MuiSelect: {
      styleOverrides: {
        root: {
          width: '100%',
          fontFamily: '"Inter", sans-serif',
          color: brandColors.paperWhite,
        },
      },
    },
    // Input Labels
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: brandColors.paperWhite,
        },
      },
    },
    // List items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
          '&:hover': {
            backgroundColor: brandColors.accentRed,
          },
          '&.Mui-selected': {
            backgroundColor: brandColors.accentRed,
            color: brandColors.paperWhite,
            '&:hover': {
              backgroundColor: '#7a0225', // Slightly lighter red for hover on selected
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
        },
        primary: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          color: brandColors.paperWhite,
        },
        secondary: {
          fontFamily: '"Inter", sans-serif',
          color: brandColors.sandPink,
        },
      },
    },
    // React Admin specific menu items
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '&:hover': {
            backgroundColor: brandColors.accentRed + ' !important',
          },
          '&.Mui-selected': {
            backgroundColor: brandColors.accentRed + ' !important',
            '&:hover': {
              backgroundColor: '#7a0225 !important',
            },
          },
        },
      },
    },
    // Menu List styling
    MuiMenuList: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          '& .MuiMenuItem-root:hover': {
            backgroundColor: brandColors.accentRed + ' !important',
          },
        },
      },
    },
    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          color: brandColors.paperWhite,
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          '&.Mui-selected': {
            color: brandColors.goldAccent,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: brandColors.goldAccent,
        },
      },
    },
    // Chips
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", sans-serif',
          fontWeight: 500,
        },
        label: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
  },
});

// Default theme export (you can switch between light/dark)
export default lightTheme;